import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import { NOTIFICATION_TYPE } from '../../../constants/common'
import { MENU_RENTAL_PORTAL_ITEM } from '../../../constants/menu'
import { ROUTE_PATH_LIST_RENTAL_PORTAL } from '../../../constants/route'
import { ScreenContainerRef } from '../../../context'
import { usePageBrowserTabTitle } from '../../../hooks'
import Button from '../../Button'
import { NotificationInline } from '../../Notification'
import { Page } from '../../Page'
import { PageEmptyState } from '../../PageEmptyState'
import { PageHeader } from '../../PageHeader'
import { SelectionToolbar } from '../../SelectionToolbar'
import {
  getSelectedSize,
  Table,
  TableControlBar,
  TableMassAction,
} from '../../Table'
import { TablePagination } from '../../TablePagination'
import Text from '../../Text'
import { getRentalPortalSubsectionByPathname } from '../RentalPortal.utils'

import {
  QA_NAME_RENTAL_PORTAL_ARCHIVE_PAGE,
  QA_NAME_RENTAL_PORTAL_ARCHIVE_REFRESH_ERROR,
  QA_NAME_RENTAL_PORTAL_ARCHIVE_RETRY,
  RENTAL_PORTAL_ARCHIVE_COLUMNS,
  RENTAL_PORTAL_ARCHIVE_SKELETON_ROWS,
} from './RentalPortalArchive.constants'
import {
  useRentalPortalArchiveMassActions,
  useRentalPortalArchiveRowActions,
  useRentalPortalArchiveTable,
} from './RentalPortalArchive.hooks'
import { getRentalPortalArchiveItemActions } from './RentalPortalArchive.utils'
import { RentalPortalArchiveTableRow } from './RentalPortalArchiveTableRow'

import * as styles from './RentalPortalArchive.module.css'

import RefreshIcon from '../../../icons/refresh.svg'

function RentalPortalArchive() {
  const { t } = useTranslation()

  const { pathname } = useLocation()

  const [modal, setModal] = useState(null)

  const {
    allItems,
    checkIsItemSelected,
    exclude,
    excludeIds,
    handleCleanSelection,
    handleExclude,
    handleRefresh,
    handleSelect,
    handleSelectAll,
    handleSelectAllPages,
    handleSetPagination,
    handleUnselectAllPages,
    isAllSelected,
    isLoading,
    isRefetching,
    isRemoveSelection,
    items,
    pageScrollWrapperRef,
    pagination,
    selectAllPages,
    selected,
    serverError,
  } = useRentalPortalArchiveTable()

  const { handleCallItemAction } = useRentalPortalArchiveRowActions({
    setModal,
    onClose: handleCloseModal,
    onReload: handleRefresh,
  })

  const { massActions, setMassAction } = useRentalPortalArchiveMassActions({
    allItems,
    excludeIds,
    selectAllPages,
    selected,
    setModal,
    onCleanSelection: handleCleanSelection,
    onClose: handleCloseModal,
    onReload: handleRefresh,
  })

  const subsection = getRentalPortalSubsectionByPathname(pathname)

  usePageBrowserTabTitle({ isLoading, pageTitle: t(subsection.title) })

  function handleCloseModal() {
    setModal(null)
  }

  const hasItems = items.length > 0

  const hasBodyError = Boolean(serverError) && !hasItems

  const hasStaleError = Boolean(serverError) && hasItems

  const breadcrumbs = [
    {
      id: MENU_RENTAL_PORTAL_ITEM.title,
      title: t(MENU_RENTAL_PORTAL_ITEM.title),
      to: `/${ROUTE_PATH_LIST_RENTAL_PORTAL.root}`,
    },
    { id: subsection.title, title: t(subsection.title) },
  ]

  return (
    <ScreenContainerRef value={pageScrollWrapperRef}>
      <Page
        pagination={
          hasItems || isLoading ? (
            <TablePagination
              responsive
              isSkeleton={isLoading}
              value={pagination}
              onChange={handleSetPagination}
            />
          ) : null
        }
        qaName={QA_NAME_RENTAL_PORTAL_ARCHIVE_PAGE}
        scrollWrapperRef={pageScrollWrapperRef}
      >
        <PageHeader
          breadcrumbs={breadcrumbs}
          isLoading={isLoading}
          itemsCount={pagination.count}
          title={t(subsection.title)}
        />
        {hasStaleError ? (
          <NotificationInline
            actions={[
              {
                beforeIconProps: { Icon: RefreshIcon },
                callback: handleRefresh,
                isLoading: isRefetching,
                text: t('RentalPortal.ListingRetryBtnKey'),
              },
            ]}
            className={styles.refreshError}
            qaName={QA_NAME_RENTAL_PORTAL_ARCHIVE_REFRESH_ERROR}
            text={t('RentalPortal.ArchiveLoadErrorKey')}
            type={NOTIFICATION_TYPE.error}
          />
        ) : null}
        {hasBodyError ? (
          <PageEmptyState EntityIcon={subsection.EmptyStateIcon}>
            <Text isSecondary>{t('RentalPortal.ArchiveLoadErrorKey')}</Text>
            <Button.Flat
              beforeIconProps={{ Icon: RefreshIcon }}
              isLoading={isRefetching}
              qaName={QA_NAME_RENTAL_PORTAL_ARCHIVE_RETRY}
              text={t('RentalPortal.ListingRetryBtnKey')}
              onClick={handleRefresh}
            />
          </PageEmptyState>
        ) : hasItems || isLoading ? (
          <>
            <TableControlBar>
              <TableMassAction
                responsive
                actions={massActions}
                isAllSelected={isAllSelected}
                isLoading={isLoading}
                isRemoveSelection={isRemoveSelection}
                selectedSize={getSelectedSize({
                  exclude,
                  pagination,
                  selectAllPages,
                  selected,
                })}
                setMassAction={setMassAction}
                onSelectAll={handleSelectAll}
              />
            </TableControlBar>
            <Table
              TableRow={RentalPortalArchiveTableRow}
              checkIsItemSelected={checkIsItemSelected}
              columns={RENTAL_PORTAL_ARCHIVE_COLUMNS}
              containerRef={pageScrollWrapperRef}
              headerRowProps={{ resizable: false }}
              isLoading={isLoading}
              items={items}
              rowProps={{
                exclude,
                getActions: getRentalPortalArchiveItemActions,
                isSelectedAll: selectAllPages,
                selected,
                onCallAction: handleCallItemAction,
                onSelect: selectAllPages ? handleExclude : handleSelect,
              }}
              skeletonRows={RENTAL_PORTAL_ARCHIVE_SKELETON_ROWS}
            />
          </>
        ) : (
          <PageEmptyState
            EntityIcon={subsection.EmptyStateIcon}
            text={t(subsection.emptyTextKey)}
          />
        )}
        {selected.length > 0 || selectAllPages ? (
          <SelectionToolbar
            allSelected={selectAllPages || selected.length === pagination.count}
            count={pagination.count}
            excludeCount={exclude.length}
            selected={selected.length}
            onClose={handleUnselectAllPages}
            onSelectAll={handleSelectAllPages}
          />
        ) : null}
        <ScreenContainerRef value={null}>{modal}</ScreenContainerRef>
      </Page>
    </ScreenContainerRef>
  )
}

export default RentalPortalArchive
